import React from 'react';
import { 
  QrCode, Store, MapPin, CreditCard, Wallet, BarChart3, 
  Shield, Bell, Settings, FileText, Users, TrendingUp 
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/user/Navbar';
import Footer from '../../components/user/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const Features: React.FC = () => {
  const [heroRef, heroVisible] = useScrollAnimation();
  const [userFeaturesRef, userFeaturesVisible] = useScrollAnimation();
  const [businessFeaturesRef, businessFeaturesVisible] = useScrollAnimation();
  const [adminFeaturesRef, adminFeaturesVisible] = useScrollAnimation();
  const [ctaRef, ctaVisible] = useScrollAnimation();
  const userFeatures = [
    {
      icon: <Store size={40} />,
      title: 'Discover Businesses',
      description: 'Find nearby stores, restaurants, salons, fuel stations, and more with real-time location tracking and filters.'
    },
    {
      icon: <MapPin size={40} />,
      title: 'Smart Navigation',
      description: 'Get turn-by-turn directions to any business with integrated maps and real-time traffic updates.'
    },
    {
      icon: <QrCode size={40} />,
      title: 'Quick QR Scan & Pay',
      description: 'Scan business QR codes and complete payments in seconds with your digital wallet.'
    },
    {
      icon: <Wallet size={40} />,
      title: 'Digital Wallet',
      description: 'Add money via Stripe, manage your balance, and track all transactions in one place.'
    },
    {
      icon: <FileText size={40} />,
      title: 'Transaction History',
      description: 'View detailed payment history, download receipts, and export transaction records.'
    },
    {
      icon: <Bell size={40} />,
      title: 'Real-time Notifications',
      description: 'Get instant alerts for successful payments, special offers, and nearby deals.'
    }
  ];

  const businessFeatures = [
    {
      icon: <QrCode size={40} />,
      title: 'Static & Dynamic QR',
      description: 'Generate both static and dynamic QR codes for flexible payment options.'
    },
    {
      icon: <CreditCard size={40} />,
      title: 'Accept Payments',
      description: 'Receive payments instantly with automatic wallet updates and notifications.'
    },
    {
      icon: <BarChart3 size={40} />,
      title: 'Analytics & Reports',
      description: 'Track sales, view peak hours, identify best-selling items, and export reports in PDF/Excel.'
    },
    {
      icon: <Wallet size={40} />,
      title: 'Business Wallet',
      description: 'Manage earnings, withdraw to bank account with instant or scheduled settlements.'
    },
    {
      icon: <Settings size={40} />,
      title: 'Dynamic Store Setup',
      description: 'Customize your store based on category with dynamic fields for services, products, and pricing.'
    },
    {
      icon: <Users size={40} />,
      title: 'Customer Insights',
      description: 'View customer statistics, ratings, reviews, and engagement metrics.'
    }
  ];

  const adminFeatures = [
    {
      icon: <Shield size={40} />,
      title: 'Business Approvals',
      description: 'Review KYC documents, verify businesses, and approve or reject registrations.'
    },
    {
      icon: <Settings size={40} />,
      title: 'Dynamic Categories',
      description: 'Create and manage business categories with custom fields and validation rules.'
    },
    {
      icon: <CreditCard size={40} />,
      title: 'Payment Monitoring',
      description: 'Monitor live transactions, handle refunds, and detect fraudulent activities.'
    },
    {
      icon: <Wallet size={40} />,
      title: 'Settlement Control',
      description: 'Approve withdrawals, manage platform wallet, and control payment settlements.'
    },
    {
      icon: <TrendingUp size={40} />,
      title: 'Platform Analytics',
      description: 'View comprehensive reports on users, businesses, revenue, and platform growth.'
    },
    {
      icon: <Users size={40} />,
      title: 'User Management',
      description: 'Manage users and businesses, suspend accounts, and resolve disputes.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section ref={heroRef} className="pt-32 pb-20 px-4 bg-white">
        <div className={`max-w-7xl mx-auto text-center transition-all duration-700 ${
          heroVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
        }`}>
          <h1 className="text-5xl md:text-6xl font-bold text-secondary-900 mb-6 tracking-tight">
            Powerful Features
          </h1>
          <p className="text-xl text-secondary-600 max-w-3xl mx-auto leading-relaxed">
            Everything you need for seamless business discovery, payments, and management
          </p>
        </div>
      </section>

      {/* User Features */}
      <section ref={userFeaturesRef} className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-700 ${
            userFeaturesVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
          }`}>
            <h2 className="text-4xl font-bold text-secondary-900 mb-4">
              For Users
            </h2>
            <p className="text-xl text-secondary-600">
              Discover, navigate, and pay with ease
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {userFeatures.map((feature, index) => (
              <div 
                key={index}
                className={`bg-white p-8 rounded-xl border-2 border-secondary-100 hover:border-primary-500 hover:shadow-soft transition-all duration-500 group hover:scale-105 hover:-translate-y-2 ${
                  userFeaturesVisible 
                    ? `animate-fade-in-up opacity-100` 
                    : 'opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="text-primary-500 mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-secondary-900 mb-3 group-hover:text-primary-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-secondary-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Features */}
      <section ref={businessFeaturesRef} className="py-20 px-4 bg-secondary-50">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-700 ${
            businessFeaturesVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
          }`}>
            <h2 className="text-4xl font-bold text-secondary-900 mb-4">
              For Businesses
            </h2>
            <p className="text-xl text-secondary-600">
              Manage your store and grow your revenue
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {businessFeatures.map((feature, index) => (
              <div 
                key={index}
                className={`bg-white p-8 rounded-xl border-2 border-secondary-100 hover:border-primary-500 hover:shadow-soft transition-all duration-500 group hover:scale-105 hover:-translate-y-2 ${
                  businessFeaturesVisible 
                    ? `animate-fade-in-up opacity-100` 
                    : 'opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="text-primary-500 mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-secondary-900 mb-3 group-hover:text-primary-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-secondary-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Admin Features */}
      <section ref={adminFeaturesRef} className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-700 ${
            adminFeaturesVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
          }`}>
            <h2 className="text-4xl font-bold text-secondary-900 mb-4">
              For Administrators
            </h2>
            <p className="text-xl text-secondary-600">
              Complete platform control and management
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {adminFeatures.map((feature, index) => (
              <div 
                key={index}
                className={`bg-white p-8 rounded-xl border-2 border-secondary-100 hover:border-primary-500 hover:shadow-soft transition-all duration-500 group hover:scale-105 hover:-translate-y-2 ${
                  adminFeaturesVisible 
                    ? `animate-fade-in-up opacity-100` 
                    : 'opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="text-primary-500 mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-secondary-900 mb-3 group-hover:text-primary-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-secondary-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="py-20 px-4 bg-primary-500">
        <div className={`max-w-4xl mx-auto text-center text-white transition-all duration-700 ${
          ctaVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
        }`}>
          <h2 className="text-4xl font-bold mb-6 tracking-tight">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Join Rita today and experience the future of digital payments
          </p>
          <Link
            to="/contact"
            className="inline-block bg-white text-primary-600 px-10 py-4 rounded-lg font-bold text-lg hover:bg-secondary-50 transition-all duration-300 shadow-xl hover:scale-110 hover:-translate-y-1"
          >
            Start Now
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Features;

